import { TDate, TObjectBaseTDate, TReadAccess } from "@propely/propely-typings";
import { z } from "zod";

export type IWorkflowActionID = string;

export const TWorkflowStepApp = z.enum(["local", "integration"]);
export type IWorkflowStepApp = z.infer<typeof TWorkflowStepApp>;

export const TWorkflowIntegration = z.object({
  companyID: z.string().nullish(),
  apiClientID: z.string().nullish(),
});
export type IWorkflowIntegration = z.infer<typeof TWorkflowIntegration>;

export const TWorkflowConditionType = z.object({
  step: z.string().nullish(),
  field: z.string().nullish(),
  customProperty: z.string().nullish(),
  comparator: z.string().nullish(),
  data: z.any(),
  dataReference: z
    .object({
      step: z.string().nullish(),
      field: z.string().nullish(),
    })
    .nullish(),
});

export type IWorkflowConditionType = z.infer<typeof TWorkflowConditionType>;
export const TWorkflowReference = z.object({
  step: z.string().nullish(),
  field: z.string().nullish(),
  conditionsList: z.array(z.object({ conditions: z.array(TWorkflowConditionType) })).nullish(),
});
export type IWorkflowReference = z.infer<typeof TWorkflowReference>;

export const TWorkflowStepCondition = z.object({
  step: z.string().nullish(),
  field: z.string().nullish(),
  customProperty: z.string().nullish(),
  comparator: z.string().nullish(),
  data: z.any(),
  dataReference: TWorkflowReference.nullish(),
});
export type IWorkflowStepCondition = z.infer<typeof TWorkflowStepCondition>;

export const TWorkflowStepConditions = z.array(
  z.object({
    conditions: z.array(TWorkflowStepCondition),
  })
);
export type IWorkflowStepConditions = z.infer<typeof TWorkflowStepConditions>;

export const TWorkflowStepAction = z.object({
  app: TWorkflowStepApp.nullish(),
  action: z.string().nullish(),
  integration: TWorkflowIntegration.nullish(),

  fields: z
    .record(
      z.string(),
      z.object({
        value: z.any().nullish(),
        valueReference: TWorkflowReference.nullish(),
      })
    )
    .nullish(),
  reference: TWorkflowReference.nullish(),
});
export type IWorkflowStepAction = z.infer<typeof TWorkflowStepAction>;

export const TWorkflowStep = z.object({
  type: z.string(),
  nextStep: z.string().nullish(),
  conditionsList: TWorkflowStepConditions.nullish(),
  action: TWorkflowStepAction.nullish(),
});
export type IWorkflowStep = z.infer<typeof TWorkflowStep>;

export type IWorkflowStepWithID = IWorkflowStep & { id: string };

export const TWorkflow = z
  .object({
    name: z.string(),
    isActive: z.boolean(),
    readAccess: TReadAccess,
    trigger: z.string(),
    companies: z.array(z.string()),
    steps: z.record(z.string(), TWorkflowStep),
    lastRun: TDate.nullish(),
    runCount: z.number().nullish(),
    startStep: z.string().nullish(),
    isEnabledForAdministratingCompanies: z.boolean().nullish(),
    properties: z.array(z.string()).nullish(),
  })
  .extend(TObjectBaseTDate.shape);
export type IWorkflow = z.infer<typeof TWorkflow>;
